import React, { useState, useEffect, useRef } from 'react';
import { Typography, Alert, Row, Col, Space, Skeleton } from 'antd';
import { getSystemDiagnostics } from './utils/nifiHelper';

const { Text } = Typography;

const METRICS = [
  { key: 'cpuUsage', label: 'CPU Load', color: '#3b82f6' },
  { key: 'memoryUsage', label: 'Memory', color: '#22c55e' },
  { key: 'diskUsage', label: 'Disk', color: '#f59e0b' },
  { key: 'networkUsage', label: 'Network', color: '#a78bfa' },
  { key: 'gcUsage', label: 'GC Time', color: '#ef4444' }
];

const ErrorStats = ({ onRefresh, userInfo }) => {
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // History for system metrics
  const historyRef = useRef({
    cpuUsage: Array(20).fill(0),
    memoryUsage: Array(20).fill(0),
    diskUsage: Array(20).fill(0),
    networkUsage: Array(20).fill(0),
    gcUsage: Array(20).fill(0)
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const nifiUsername = userInfo?.nifiUsername;
      const nifiPassword = userInfo?.nifiPassword;
      
      if (!nifiUsername || !nifiPassword) {
        throw new Error('NiFi credentials not configured');
      }
      
      const sysData = await getSystemDiagnostics(nifiUsername, nifiPassword);

      // Process system metrics
      const sysSnapshot = sysData?.systemDiagnostics?.aggregateSnapshot || {};
      const heapUtilization = parseFloat((sysSnapshot.heapUtilization || '0%').replace('%', ''));
      const flowFileRepo = sysSnapshot.flowFileRepositoryStorageUsage || {};
      const diskUtilization = parseFloat((flowFileRepo.utilization || '0%').replace('%', ''));
      const gcCollections = sysSnapshot.garbageCollection || [];
      const totalGcMillis = gcCollections.reduce((total, gc) => total + (gc.collectionMillis || 0), 0);

      const metrics = {
        cpuUsage: Number(sysSnapshot.processorLoadAverage || 0),
        memoryUsage: heapUtilization,
        diskUsage: diskUtilization,
        networkUsage: 0, // Not available in current API response
        gcUsage: totalGcMillis / 1000, // Convert to seconds
      };

      setSystemMetrics(metrics);

      // Update history
      METRICS.forEach(({ key }) => {
        const arr = historyRef.current[key];
        arr.push(metrics[key]);
        if (arr.length > 20) arr.shift();
      });

    } catch (err) {
      setError(`Failed to fetch system metrics: ${err.message}`);
      console.error('Error fetching system metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        padding: '12px', 
        height: '400px', 
        width: '100%'
      }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Skeleton active title={{ width: '30%' }} paragraph={{ rows: 1, width: ['45%'] }} />
          <Skeleton active paragraph={{ rows: 6 }} />
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '12px', height: '400px' }}>
        <Alert
          message="Error Loading System Metrics"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* System Metrics Section */}
      {systemMetrics && (
        <div>
          <Row style={{ margin: 0 }}>
            {METRICS.map(({ key, label, color }) => {
              const value = systemMetrics[key];
              const displayValue = key === 'gcUsage' ? value.toFixed(2) : value.toFixed(1);
              const suffix = key === 'gcUsage' ? 's' : (key === 'cpuUsage' ? '' : '%');
              
              // Calculate percentage for progress bar
              let percentage = 0;
              if (key === 'gcUsage') {
                percentage = Math.min((value / 10) * 100, 100);
              } else if (key === 'cpuUsage') {
                percentage = Math.min((value / 4) * 100, 100);
              } else {
                percentage = Math.min(value, 100);
              }
              
              return (
                <Col style={{ width: '20%', padding: '0 8px' }} key={key}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
                      {label}
                    </Text>
                    <div style={{ 
                      height: 80, 
                      width: '100%', 
                      position: 'relative',
                      borderRadius: 8,
                      border: '1px solid #e6e6e6',
                      overflow: 'hidden'
                    }}>
                      {/* Progress bar fill */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: `${percentage}%`,
                        background: `linear-gradient(to top, ${color}, ${color}90)`,
                        borderRadius: '0 0 7px 7px',
                        transition: 'height 0.3s ease-in-out'
                      }} />
                      
                      {/* Value overlay */}
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '2px'
                      }}>
                        <span>{displayValue}</span>
                        <span style={{ fontSize: '12px', opacity: 0.9 }}>
                          {suffix}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </div>
  );
};

export default ErrorStats; 